import { Controller } from "@hotwired/stimulus"

// Within one scope, a value picked by one select is off the menu in the others:
// the mapping iteration writes each target field once, so a field already taken
// is gone from the other rows' pickers.
//
// Hidden, not disabled — a greyed-out option still reads as a choice, and the
// feedback asked for a shorter list, not a longer one with dead entries. Only
// the selects that declare themselves a target take part, so the iteration's
// condition pickers, which sit in the same scope and may repeat a field, are
// untouched.
//
// Rows are pending until the screen's one save, so this runs entirely
// client-side; the rule behind it lives on FileLayoutDraft::MappingIteration
// for a payload that arrives without the browser's help.
export default class extends Controller {
  static targets = ["select", "add"]

  // The rows the block arrives with are what is stored, or what a refused save
  // just sent back — neither may be rewritten behind the operator's back, or
  // the row an error names no longer holds the value it was named for. So only
  // a row added afterwards claims a free field: the flag flips once the initial
  // targets have connected.
  connect() {
    this.settled = false
    queueMicrotask(() => {
      this.settled = true
    })
  }

  selectTargetConnected(select) {
    if (this.settled) this.claimFreeOption(select)
    this.refresh()
  }

  // A row cloned from the hidden template starts on the template's field, which
  // this iteration may already write — so it would open on a duplicate the save
  // then refuses. Move it to the first field still free instead. Only a row
  // that has never been saved (a persisted one carries a _destroy field) is
  // moved: what is stored stays on screen exactly as it is stored.
  claimFreeOption(select) {
    const row = select.closest("[data-pending-rows-row]")
    if (!row || row.querySelector("[data-pending-rows-destroy]")) return

    const taken = new Set(this.activeSelects().filter((other) => other !== select).map((other) => other.value))
    if (!taken.has(select.value)) return

    const free = Array.from(select.options).find((option) => !taken.has(option.value))
    if (!free) return

    select.value = free.value
    select.dispatchEvent(new Event("change", { bubbles: true }))
  }

  selectTargetDisconnected() {
    this.refresh()
  }

  refresh() {
    const taken = new Set(this.activeSelects().map((select) => select.value))

    this.selectTargets.forEach((select) => {
      Array.from(select.options).forEach((option) => {
        option.hidden = option.value !== select.value && taken.has(option.value)
      })
    })

    this.updateAddButton(taken)
  }

  // With every field taken, another row could only be a duplicate — one the
  // save refuses and that no picker can move off, since every option it could
  // move to is taken as well. So the row is not offered in the first place.
  updateAddButton(taken) {
    if (!this.hasAddTarget || this.selectTargets.length === 0) return

    this.addTarget.disabled = taken.size >= this.selectTargets[0].options.length
  }

  // A removed row keeps its select in the DOM until the save — that is how a
  // persisted row carries its _destroy field — so what it used to write is
  // free again the moment it is hidden.
  activeSelects() {
    return this.selectTargets.filter((select) => {
      const row = select.closest("[data-pending-rows-row]")
      return !row || !row.hidden
    })
  }
}
